import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Slider } from '@/components/ui/slider';
import audioService from '@/services/audioService';
import api from '@/services/api';
import userManager from '@/services/userManager';
import { Volume2, VolumeX, Headphones, CheckCircle, ChevronLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner'; // BUILD 65: Import toast for volume reminder
// ClinicalTestUpload removed for GDPR/HDS compliance

const TinnitusTest = ({ t }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [frequency, setFrequency] = useState(4000);
  const [detectedFrequency, setDetectedFrequency] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [volumeDbSpl, setVolumeDbSpl] = useState(1); // V1.1.2: Start very low — tinnitus is often subtle
  const [step1Volume, setStep1Volume] = useState(null); // Store volume from step 1 for zoomed slider
  const [playingExampleSound, setPlayingExampleSound] = useState(null); // For example sounds
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccessfully, setSavedSuccessfully] = useState(false); // Track if we're saving data
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);
  const gainNodeRef = useRef(null);

  // Step 1: Broad sweep 500 - 8000 Hz
  const step1Min = 500;
  const step1Max = 8000;
  
  // Step 2: Fine-tuning ±100 Hz
  const [step2Min, setStep2Min] = useState(3900);
  const [step2Max, setStep2Max] = useState(4100);
  
  // Step 2: Zoomed volume slider ±10 dB centered on step 1 volume
  const getZoomedVolumeRange = () => {
    if (step1Volume === null) return { min: 0, max: 30 };
    const min = Math.max(0, step1Volume - 8);
    const max = Math.min(30, step1Volume + 8);
    return { min, max };
  };

  useEffect(() => {
    return () => {
      audioService.stopTone();
      stopExampleSound();
    };
  }, []);

  const stopExampleSound = () => {
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
        oscillatorRef.current.disconnect();
      } catch (e) {
        // Already stopped
      }
      oscillatorRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }
    setPlayingExampleSound(null);
  };

  const playExampleSound = (type) => {
    // Stop any currently playing sound
    stopExampleSound();
    
    // Create audio context if needed
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    const ctx = audioContextRef.current;
    
    setPlayingExampleSound(type.id);
    
    // Create gain node for volume control
    const gainNode = ctx.createGain();
    gainNode.gain.value = 0.1; // 10% volume for comfort
    gainNode.connect(ctx.destination);
    gainNodeRef.current = gainNode;
    
    if (type.frequency === 'noise') {
      // White noise
      const bufferSize = 2 * ctx.sampleRate;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      
      const whiteNoise = ctx.createBufferSource();
      whiteNoise.buffer = noiseBuffer;
      whiteNoise.loop = true;
      whiteNoise.connect(gainNode);
      whiteNoise.start(0);
      oscillatorRef.current = whiteNoise;
      
      setTimeout(() => stopExampleSound(), 3000);
      
    } else if (type.frequency === 'click') {
      // Clicking sound
      let clickCount = 0;
      const clickInterval = setInterval(() => {
        if (clickCount >= 6) {
          clearInterval(clickInterval);
          stopExampleSound();
          return;
        }
        
        const osc = ctx.createOscillator();
        const clickGain = ctx.createGain();
        osc.frequency.value = 2000;
        osc.connect(clickGain);
        clickGain.connect(gainNode);
        
        clickGain.gain.setValueAtTime(0.5, ctx.currentTime);
        clickGain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05);
        
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.05);
        clickCount++;
      }, 500);
      
    } else if (type.frequency === 'pulse') {
      // Pulsatile
      const osc = ctx.createOscillator();
      osc.frequency.value = 200;
      osc.type = 'sine';
      
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 1.2;
      
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.3;
      
      lfo.connect(lfoGain);
      lfoGain.connect(gainNode.gain);
      
      osc.connect(gainNode);
      osc.start(0);
      lfo.start(0);
      oscillatorRef.current = osc;
      
      setTimeout(() => stopExampleSound(), 3000);
      
    } else if (type.frequency === 'modulated') {
      // Modulated whistle
      const osc = ctx.createOscillator();
      osc.frequency.value = 8000;
      osc.type = 'sine';
      
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.5;
      
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 1000;
      
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      
      osc.connect(gainNode);
      osc.start(0);
      lfo.start(0);
      oscillatorRef.current = osc;
      
      setTimeout(() => stopExampleSound(), 3000);
      
    } else {
      // Pure tone
      const osc = ctx.createOscillator();
      osc.frequency.value = type.frequency;
      osc.type = 'sine';
      osc.connect(gainNode);
      osc.start(0);
      oscillatorRef.current = osc;
      
      setTimeout(() => stopExampleSound(), 3000);
    }
  };

  // Convert dB SPL to volume (0-1 range)
  // V1.1.2: Max 0.15 (safe for tinnitus), exponential curve for ultra-fine low-end control
  const dbSplToVolume = (dbSpl) => {
    if (dbSpl <= 0) return 0;
    // Exponential curve: 90% of slider range covers low volumes
    // At 30 → 0.15, at 20 → 0.05, at 10 → 0.015, at 5 → 0.005
    const normalized = dbSpl / 30;
    const curved = Math.pow(normalized, 3);
    return curved * 0.15;
  };

  const playFrequency = async () => {
    const volume = dbSplToVolume(volumeDbSpl);
    await audioService.playTone(frequency, volume, 'both'); // playTone now handles masterGain internally
    setIsPlaying(true);
  };

  const stopPlaying = () => {
    audioService.stopTone();
    setIsPlaying(false);
  };

  const handleFrequencyChange = async (value) => {
    setFrequency(value[0]);
    if (isPlaying) {
      const volume = dbSplToVolume(volumeDbSpl);
      await audioService.playTone(value[0], volume, 'both'); // Always stereo - MUST await
    }
  };

  const handleVolumeChange = async (value) => {
    setVolumeDbSpl(value[0]);
    if (isPlaying) {
      const volume = dbSplToVolume(value[0]);
      console.log(`   → Calculated volume: ${volume.toFixed(6)} (isPlaying=${isPlaying})`);
      await audioService.playTone(frequency, volume, 'both'); // Always stereo - MUST await
    } else {
      console.log(`   → Not playing, volume change will apply when playback starts`);
    }
  };

  const handleMatch = () => {
    if (step === 1) {
      // Store step 1 volume for zoomed slider in step 2
      setStep1Volume(volumeDbSpl);
      
      // Move to step 2 with fine-tuning
      setDetectedFrequency(frequency);
      setStep2Min(Math.max(500, frequency - 100));
      setStep2Max(Math.min(8000, frequency + 100));
      setStep(2);
      stopPlaying();
      
      // Automatically restart playback in step 2 after a short delay
      setTimeout(async () => {
        const volume = dbSplToVolume(volumeDbSpl);
        await audioService.playTone(frequency, volume, 'both');
        setIsPlaying(true);
      }, 500);
    } else {
      // Test complete - move to type selection screen
      setDetectedFrequency(frequency);
      setTestComplete(true);
      stopPlaying();
    }
  };

  const saveTestData = async () => {
    console.log('💾 [TinnitusTest] Starting saveTestData...');
    
    let userId;
    try {
      userId = await userManager.getUserId();
      console.log('💾 [TinnitusTest] Got userId:', userId);
    } catch (e) {
      console.error('💾 [TinnitusTest] Error getting userId:', e);
      throw new Error('Impossible de récupérer l\'ID utilisateur. Veuillez vous reconnecter.');
    }
    
    // Validate required fields
    if (!detectedFrequency) {
      throw new Error(t?.frequencyNotDetected || 'Fréquence non détectée. Veuillez refaire le test.');
    }
    
    if (!userId) {
      throw new Error('User ID not found. Please log in again.');
    }
    
    // Calculate adaptive gain based on tinnitus volume
    // Direct proportional relationship: gain = volume / 5
    // This ensures consistency: 4 dB SPL → ~1 dB gain, 30 dB SPL → 6 dB gain, 60 dB SPL → 12 dB gain
    const calculateAdaptiveGain = (volumeSpl) => {
      // Simple ratio: gain is 1/5 of the measured volume
      // This creates a transparent, predictable relationship
      const gain = volumeSpl / 5;
      
      // Round to nearest integer, minimum 1 dB if volume > 0
      return volumeSpl > 0 ? Math.max(1, Math.round(gain)) : 0;
    };
    
    const adaptiveGain = calculateAdaptiveGain(volumeDbSpl);
    
    const payload = {
      user_id: userId,
      ear: 'both', // Always stereo
      detected_frequency: detectedFrequency, // Use detected frequency from test completion, not current slider value
      confidence_level: adaptiveGain, // Adaptive gain based on measured volume
      tinnitus_type: selectedType,
      volume_db_spl: volumeDbSpl
    };
    
    console.log('💾 [TinnitusTest] Saving tinnitus test:', payload);
    
    try {
      // P1: Check if a test already exists for today
      let replaceToday = false;
      try {
        const existingToday = await api.getTodayTinnitusTest(userId);
        if (existingToday) {
          console.log('💾 [TinnitusTest] Existing test found for today:', existingToday);
          const lang = localStorage.getItem('hearen_lang') || 'fr';
          const confirmMsg = lang === 'en' 
            ? 'You already have a test from today. This new test will replace today\'s measurement. Continue?'
            : 'Vous avez déjà un test aujourd\'hui. Ce nouveau test remplacera la mesure du jour. Continuer ?';
          
          const confirmed = window.confirm(confirmMsg);
          if (!confirmed) {
            console.log('💾 [TinnitusTest] User cancelled replacement');
            return null; // User cancelled
          }
          replaceToday = true;
        }
      } catch (checkError) {
        // If check fails, continue with normal save
        console.log('💾 [TinnitusTest] No existing test today or check failed');
      }
      
      const result = await api.createTinnitusTest(payload, replaceToday);
      console.log('💾 [TinnitusTest] Save successful:', result);
      return result;
    } catch (apiError) {
      console.error('💾 [TinnitusTest] API error:', apiError);
      console.error('💾 [TinnitusTest] API error type:', typeof apiError);
      console.error('💾 [TinnitusTest] API error stringified:', JSON.stringify(apiError, null, 2));
      
      // Extract error message from various possible formats
      let detail = 'Erreur serveur inconnue';
      
      if (typeof apiError === 'string') {
        detail = apiError;
      } else if (apiError?.response?.data?.detail) {
        detail = apiError.response.data.detail;
      } else if (apiError?.response?.data?.message) {
        detail = apiError.response.data.message;
      } else if (apiError?.response?.data) {
        // If response.data is an object without detail/message, stringify it
        detail = typeof apiError.response.data === 'object' 
          ? JSON.stringify(apiError.response.data) 
          : String(apiError.response.data);
      } else if (apiError?.data?.detail) {
        detail = apiError.data.detail;
      } else if (apiError?.message) {
        detail = apiError.message;
      } else if (apiError?.detail) {
        detail = apiError.detail;
      } else if (typeof apiError === 'object') {
        // Last resort: stringify the object to avoid [object Object]
        detail = JSON.stringify(apiError);
      }
      
      console.error('💾 [TinnitusTest] Extracted error detail:', detail);
      throw new Error(detail);
    }
  };

  const saveResults = async () => {
    // Check if tinnitus type is selected
    if (!selectedType) {
      alert(t.pleaseSelectTinnitusType || 'Veuillez sélectionner un type d\'acouphène avant de continuer.');
      return;
    }
    
    if (isSaving) return; // Prevent double-click
    
    // BUILD 65: Detect current language for volume reminder
    const isFr = (t?.language || document.documentElement.lang || 'fr').toLowerCase().startsWith('fr');
    
    try {
      setIsSaving(true);
      const result = await saveTestData();
      
      // P1: User cancelled replacement - stop here
      if (result === null) {
        setIsSaving(false);
        return;
      }
      
      // BUILD 65: Show volume reminder toast after test completion
      toast.info(
        isFr 
          ? 'Pensez à réduire le volume de votre appareil avant de continuer.'
          : 'Remember to lower your device volume before continuing.',
        { duration: 5000 }
      );
      
      // Success - show confirmation screen
      setSavedSuccessfully(true);
    } catch (error) {
      console.error('❌ Error saving tinnitus test results:', error);
      
      // Extract error message - error is always an Error object from saveTestData
      const errorMsg = error?.message || 'Erreur inconnue lors de la sauvegarde';
      
      alert(`Erreur lors de la sauvegarde: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const saveAndGoHome = async () => {
    // Check if tinnitus type is selected
    if (!selectedType) {
      alert(t.pleaseSelectTinnitusType || 'Veuillez sélectionner un type d\'acouphène avant de continuer.');
      return;
    }
    
    if (isSaving) return; // Prevent double-click
    
    // BUILD 65: Detect current language for volume reminder
    const isFr = (t?.language || document.documentElement.lang || 'fr').toLowerCase().startsWith('fr');
    
    try {
      setIsSaving(true);
      const result = await saveTestData();
      
      // P1: User cancelled replacement - stop here
      if (result === null) {
        setIsSaving(false);
        return;
      }
      
      // BUILD 65: Show volume reminder toast after test completion
      toast.info(
        isFr 
          ? 'Pensez à réduire le volume de votre appareil avant de continuer.'
          : 'Remember to lower your device volume before continuing.',
        { duration: 5000 }
      );
      
      // Success - navigate (isSaving will be reset on unmount anyway)
      navigate('/');
    } catch (error) {
      console.error('❌ Error saving tinnitus test results:', error);
      
      // Extract error message - error is always an Error object from saveTestData
      const errorMsg = error?.message || 'Erreur inconnue lors de la sauvegarde';
      
      alert(`Erreur lors de la sauvegarde: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (testComplete) {
    // Tinnitus types with icons and sound examples
    const tinnitusTypes = [
      { id: 'high_pitch', label: t.tinnitusTypeHighPitch || 'Sifflement aigu', icon: '📡', frequency: 10000 },
      { id: 'low_hum', label: t.tinnitusTypeLowHum || 'Bourdonnement grave', icon: '🔊', frequency: 150 },
      { id: 'clicking', label: t.tinnitusTypeClicking || 'Clic/Craquement', icon: '⚡', frequency: 'click' },
      { id: 'pulsatile', label: t.tinnitusTypePulsatile || 'Pulsatile (battement)', icon: '❤️', frequency: 'pulse' },
      { id: 'modulated', label: t.tinnitusTypeModulated || 'Sifflement modulé', icon: '〰️', frequency: 'modulated' },
      { id: 'white_noise', label: t.tinnitusTypeWhiteNoise || 'Bruit blanc/Statique', icon: '📻', frequency: 'noise' }
    ];

    if (!savedSuccessfully) {
      const isFr = !t?.locale || t?.locale !== 'en-US';
      return (
        <div className="min-h-screen bg-[#0a0a0f] pt-20 px-6 pb-40">
          <div className="max-w-sm mx-auto space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-white text-xl font-medium">
                {isFr ? 'Quel son s\'en rapproche le plus ?' : 'Which one feels closest?'}
              </h1>
            </div>

            <div className="space-y-3">
              {tinnitusTypes.map((type) => {
                const isSelected = selectedType === type.id;
                const isPlayingExample = playingExampleSound === type.id;
                
                return (
                  <div
                    key={type.id}
                    className="relative rounded-xl transition-all"
                    style={{
                      background: isSelected ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                      border: isSelected ? '1px solid rgba(99,102,241,0.3)' : '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <button
                      onClick={() => setSelectedType(type.id)}
                      className="w-full p-4 text-left flex items-center gap-3"
                    >
                      <div className="text-2xl flex-shrink-0">{type.icon}</div>
                      <div className={`flex-1 font-medium text-sm ${isSelected ? 'text-indigo-300' : 'text-white/70'}`}>
                        {type.label}
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                        isSelected ? 'bg-indigo-500/50 border-indigo-400' : 'border-white/15'
                      }`}>
                        {isSelected && <CheckCircle className="w-3 h-3 text-indigo-300" />}
                      </div>
                    </button>
                    
                    <div className="absolute top-4 right-12">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isPlayingExample) { stopExampleSound(); } else { playExampleSound(type); }
                        }}
                        className="p-1.5 rounded-full transition-all"
                        style={{
                          background: isPlayingExample ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.05)',
                        }}
                      >
                        {isPlayingExample ? (
                          <VolumeX size={16} className="text-indigo-300" />
                        ) : (
                          <Volume2 size={16} className="text-white/40" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                onClick={() => setTestComplete(false)} 
                className="flex-1 rounded-xl py-3"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                {isFr ? 'Retour' : 'Back'}
              </Button>
              <Button 
                onClick={saveResults}
                disabled={!selectedType || isSaving}
                className="flex-1 rounded-xl py-3 font-semibold"
                style={{ background: selectedType ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.05)', color: selectedType ? '#86efac' : 'rgba(255,255,255,0.3)', border: selectedType ? '1px solid rgba(34,197,94,0.2)' : '1px solid rgba(255,255,255,0.06)' }}
                data-testid="save-tinnitus-btn"
              >
                {isSaving ? '...' : (isFr ? 'Enregistrer' : 'Save')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    const isFr = !t?.locale || t?.locale !== 'en-US';
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex flex-col items-center justify-center p-6">
        <div className="max-w-sm mx-auto text-center space-y-8">
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.15)' }}>
            <CheckCircle className="h-7 w-7 text-green-400" />
          </div>

          <div className="space-y-3">
            <h1 className="text-white text-xl font-medium">
              {isFr ? 'Hearen s\'est ajusté pour toi' : 'Hearen has adjusted to you'}
            </h1>
            <p className="text-white/60 text-sm">
              {isFr ? 'Tu peux commencer' : 'You can start'}
            </p>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={async () => {
                try { await saveTestData(); } catch(e) { console.warn('Save failed:', e); }
                navigate('/history?tab=tinnitus');
              }}
              className="flex-1 rounded-xl py-3 font-semibold"
              style={{ background: 'rgba(99,102,241,0.3)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.2)' }}
              data-testid="save-tinnitus-results"
            >
              {isFr ? 'Continuer' : 'Continue'}
            </Button>
            <Button 
              onClick={async () => {
                try { await saveTestData(); } catch(e) { console.warn('Save failed:', e); }
                navigate('/');
              }}
              className="flex-1 rounded-xl py-3"
              style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              {isFr ? 'Accueil' : 'Home'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Direct test mode — V1.1.2: Gentle, non-technical
  const isFr = !t?.locale || t?.locale !== 'en-US';
  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20 px-6 pb-40">
      <div className="max-w-sm mx-auto">
        <button
          onClick={() => {
            audioService.stopTone();
            navigate('/');
          }}
          className="mb-4 p-2 rounded-lg transition-colors"
          style={{ background: 'rgba(255,255,255,0.05)' }}
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5 text-white/40" />
        </button>
        
        <div className="space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-white text-xl font-medium">
              {isFr ? 'On se rapproche doucement' : 'Let\'s gently get closer'}
            </h1>
            <p className="text-white/60 text-sm">
              {isFr ? 'Ajuste jusqu\'à ce que ça se rapproche de ce que tu entends' : 'Adjust until it feels close to what you hear'}
            </p>
          </div>

          {/* Reassuring message */}
          <div className="text-center space-y-1">
            <p className="text-white/50 text-xs">
              {isFr ? 'Tu peux y aller doucement' : 'Take your time'}
            </p>
            <p className="text-white/50 text-xs">
              {isFr ? 'Tu peux t\'arrêter à tout moment' : 'You can stop anytime'}
            </p>
          </div>

          {/* Volume reminder */}
          {step === 1 && (
            <div
              className="text-center px-4 py-3 rounded-xl mx-auto"
              style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.12)' }}
            >
              <p className="text-white/60 text-xs font-medium">
                {isFr ? 'Mets le volume de ton appareil au maximum' : 'Set your device volume to maximum'}
              </p>
              <p className="text-white/50 text-[11px] mt-0.5">
                {isFr ? 'Hearen ajuste ensuite pour toi' : 'Hearen will adjust it for you'}
              </p>
            </div>
          )}

          {/* Frequency slider — no Hz label shown */}
          <div className="space-y-4">
            <div className="px-2">
              <label className="block text-sm font-medium text-white/60 mb-3">
                {isFr ? 'Hauteur du son' : 'Sound pitch'}
              </label>
              <Slider
                value={[frequency]}
                onValueChange={handleFrequencyChange}
                min={step === 1 ? step1Min : step2Min}
                max={step === 1 ? step1Max : step2Max}
                step={step === 1 ? 50 : 10}
                className="w-full"
                data-testid="frequency-slider"
              />
              <div className="flex justify-between text-xs text-white/30 mt-2">
                <span>{isFr ? 'Grave' : 'Low'}</span>
                <span>{isFr ? 'Aigu' : 'High'}</span>
              </div>
            </div>

            <div className="px-2 pt-2">
              <label className="block text-sm font-medium text-white/60 mb-3">
                {isFr ? 'Intensité' : 'Intensity'}
              </label>
              
              {/* Step 1: Full range slider */}
              {step === 1 && (
                <>
                  <Slider
                    value={[volumeDbSpl]}
                    onValueChange={handleVolumeChange}
                    min={0}
                    max={30}
                    step={0.2}
                    className="w-full"
                    data-testid="volume-slider"
                  />
                  <div className="flex justify-between text-xs text-white/30 mt-2">
                    <span>{isFr ? 'Faible' : 'Soft'}</span>
                    <span>{isFr ? 'Fort' : 'Loud'}</span>
                  </div>
                </>
              )}
              
              {/* Step 2: Zoomed slider */}
              {step === 2 && (
                <>
                  <div className="relative">
                    <Slider
                      value={[volumeDbSpl]}
                      onValueChange={handleVolumeChange}
                      min={getZoomedVolumeRange().min}
                      max={getZoomedVolumeRange().max}
                      step={0.2}
                      className="w-full"
                      data-testid="volume-slider-zoomed"
                    />
                    {step1Volume !== null && (
                      <div 
                        className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-indigo-500/50 pointer-events-none"
                        style={{
                          left: `${((step1Volume - getZoomedVolumeRange().min) / (getZoomedVolumeRange().max - getZoomedVolumeRange().min)) * 100}%`
                        }}
                      />
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-white/30 mt-2">
                    <span>{isFr ? 'Moins fort' : 'Softer'}</span>
                    <span>{isFr ? 'Plus fort' : 'Louder'}</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                onClick={isPlaying ? stopPlaying : playFrequency}
                className="flex-1 rounded-xl py-3"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid="play-frequency-btn"
              >
                <Volume2 className="mr-2 h-4 w-4" />
                {isPlaying ? (isFr ? 'Pause' : 'Pause') : (isFr ? 'Écouter' : 'Listen')}
              </Button>
              <Button
                onClick={handleMatch}
                className="flex-1 rounded-xl py-3 font-semibold"
                style={{ background: 'rgba(99,102,241,0.3)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.2)' }}
                data-testid="match-frequency-btn"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {isFr ? 'Ça s\'en rapproche' : 'This feels close'}
              </Button>
            </div>
          </div>

          <p className="text-white/60 text-xs text-center">
            {isFr ? 'Tout reste dans une zone confortable' : 'Everything stays in a comfortable range'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TinnitusTest;